import React from 'react'
import { ExclamationTriangleIcon, NoSymbolIcon, ClockIcon } from '@heroicons/react/24/outline'
import { User } from '@/types/auth'

interface AccountStatusBannerProps {
    user: User
    className?: string
}

const AccountStatusBanner: React.FC<AccountStatusBannerProps> = ({ user, className = '' }) => {
    // Don't show banner if account is active and not banned/suspended
    if (user.isActive && !user.isBanned && !user.isSuspended) {
        return null
    }

    const getBannerContent = () => {
        if (user.isBanned) {
            return {
                type: 'banned' as const,
                icon: NoSymbolIcon,
                bgColor: 'bg-red-50',
                borderColor: 'border-red-200',
                textColor: 'text-red-800',
                iconColor: 'text-red-600',
                title: 'Account Banned',
                message: user.bannedReason
                    ? `Your account has been permanently banned. Reason: ${user.bannedReason}`
                    : 'Your account has been permanently banned.',
                action: 'Contact support if you believe this is an error.'
            }
        }

        if (user.isSuspended) {
            const endDate = user.suspensionEndDate
                ? new Date(user.suspensionEndDate).toLocaleDateString()
                : 'further notice'

            return {
                type: 'suspended' as const,
                icon: ClockIcon,
                bgColor: 'bg-yellow-50',
                borderColor: 'border-yellow-200',
                textColor: 'text-yellow-800',
                iconColor: 'text-yellow-600',
                title: 'Account Suspended',
                message: user.suspensionReason
                    ? `Your account is temporarily suspended until ${endDate}. Reason: ${user.suspensionReason}`
                    : `Your account is temporarily suspended until ${endDate}.`,
                action: 'Some features may be limited during suspension.'
            }
        }

        if (!user.isActive) {
            return {
                type: 'deactivated' as const,
                icon: ExclamationTriangleIcon,
                bgColor: 'bg-gray-50',
                borderColor: 'border-gray-200',
                textColor: 'text-gray-800',
                iconColor: 'text-gray-600',
                title: 'Account Deactivated',
                message: 'Your account has been deactivated.',
                action: 'Contact support to reactivate your account.'
            }
        }

        return null
    }

    const bannerContent = getBannerContent()
    if (!bannerContent) return null

    const { icon: Icon, bgColor, borderColor, textColor, iconColor, title, message, action } = bannerContent

    return (
        <div className={`${bgColor} ${borderColor} border rounded-lg p-4 mb-4 ${className}`}>
            <div className="flex items-start space-x-3">
                <Icon className={`h-6 w-6 ${iconColor} flex-shrink-0 mt-0.5`} />
                <div className="flex-1">
                    <h3 className={`font-semibold ${textColor} text-sm`}>
                        {title}
                    </h3>
                    <p className={`${textColor} text-sm mt-1`}>
                        {message}
                    </p>
                    <p className={`${textColor} text-xs mt-2 opacity-75`}>
                        {action}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default AccountStatusBanner